import { PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { BillingCycles, GearbeltPlanType, IAccount, SubscriptionStatuses, SubscriptionTerms } from '@site-mate/sitemate-global-shared/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { SettingsFormSkeleton } from '@/components/settings/settings-form-skeleton';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toaster';
import { getSitemateStartAccountQueryOptions, sitemateStartAccountKeys } from '@/queries/account';
import { accountService } from '@/services/global-api/account.service';
import { useUserStore } from '@/stores/user.store';

const formSchema = z.object({
  trialEndDate: z.date().optional(),
  planType: z
    .object({
      label: z.nativeEnum(GearbeltPlanType),
      value: z.nativeEnum(GearbeltPlanType),
    })
    .optional(),
  subscriptionStatus: z
    .object({
      label: z.nativeEnum(SubscriptionStatuses),
      value: z.nativeEnum(SubscriptionStatuses),
    })
    .optional(),
  initialSubscriptionDate: z.date().optional(),
  subscriptionTerm: z
    .object({
      label: z.nativeEnum(SubscriptionTerms),
      value: z.nativeEnum(SubscriptionTerms),
    })
    .optional(),
  billingCycle: z
    .object({
      label: z.nativeEnum(BillingCycles),
      value: z.nativeEnum(BillingCycles),
    })
    .optional(),

  assetsPaid: z.string().optional(),
  toolsAndEquipmentPaid: z.string().optional(),
});

function SettingsSitemateStartGearbeltBillingPage() {
  const { org } = useUserStore();

  const { data: account, isLoading } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Gearbelt billing</h1>
          <p className="text-default-text text-sm">These billing settings apply to your Gearbelt subscription only, and affect all your Gearbelt workspaces.</p>
          {(isLoading || !account) && <SettingsFormSkeleton rows={10} />}
          {account && <SettingsSitemateStartGearbeltBillingForm account={account} />}
        </div>
      </ScrollArea>
    </div>
  );
}

function SettingsSitemateStartGearbeltBillingForm({ account }: { account: IAccount }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useUserStore();

  const formInstance = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      trialEndDate: account.billing?.gearbelt?.trialEndDate ? new Date(account.billing?.gearbelt?.trialEndDate) : undefined,
      planType: account.billing?.gearbelt?.planType
        ? {
            label: account.billing?.gearbelt?.planType,
            value: account.billing?.gearbelt?.planType,
          }
        : undefined,
      subscriptionStatus: account.billing?.gearbelt?.subscriptionStatus
        ? {
            label: account.billing?.gearbelt?.subscriptionStatus,
            value: account.billing?.gearbelt?.subscriptionStatus,
          }
        : undefined,
      initialSubscriptionDate: account.billing?.gearbelt?.initialSubscriptionDate ? new Date(account.billing?.gearbelt?.initialSubscriptionDate) : undefined,
      subscriptionTerm: account.billing?.gearbelt?.subscriptionTerm
        ? {
            label: account.billing?.gearbelt?.subscriptionTerm,
            value: account.billing?.gearbelt?.subscriptionTerm,
          }
        : undefined,
      billingCycle: account.billing?.gearbelt?.billingCycle
        ? {
            label: account.billing?.gearbelt?.billingCycle,
            value: account.billing?.gearbelt?.billingCycle,
          }
        : undefined,

      assetsPaid: account.billing?.gearbelt?.assetsPaid ? account.billing?.gearbelt?.assetsPaid.toString() : '0',
      toolsAndEquipmentPaid: account.billing?.gearbelt?.toolsAndEquipmentPaid ? account.billing?.gearbelt?.toolsAndEquipmentPaid.toString() : '0',
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty },
  } = formInstance;

  const { mutateAsync: updateAccount } = useMutation({
    mutationFn: accountService.updateGearbeltBillingSettings,
    onSuccess: async () => {
      toast.success('Account updated successfully');
      await queryClient.invalidateQueries({
        queryKey: sitemateStartAccountKeys.detail(account._id),
      });
    },
    onError: () => {
      toast.error('Failed to update account');
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    if (!data.planType) {
      toast.error('Plan type is required');
      return;
    }

    await updateAccount({
      accountId: account._id,
      trialEndDate: data.trialEndDate?.toISOString() ?? undefined,
      planType: data.planType?.value,
      subscriptionStatus: data.subscriptionStatus?.value,
      initialSubscriptionDate: data.initialSubscriptionDate?.toISOString() ?? undefined,
      subscriptionTerm: data.subscriptionTerm?.value,
      billingCycle: data.billingCycle?.value,
      assetsPaid: data.assetsPaid ? +data.assetsPaid : undefined,
      toolsAndEquipmentPaid: data.toolsAndEquipmentPaid ? +data.toolsAndEquipmentPaid : undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Controller
        control={control}
        name="trialEndDate"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt trial end date</div>
            <DatePicker
              placeholder="Select trial end date..."
              date={value}
              setDate={(date) => onChange(date)}
              error={!!error}
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="planType"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt plan type</div>
            <Select
              aria-label="Gearbelt plan type"
              className="flex-1"
              value={value}
              onChange={onChange}
              options={Object.values(GearbeltPlanType).map((value) => ({ label: value, value }))}
              error={!!error}
              isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="subscriptionStatus"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt subscription status</div>
            <Select
              aria-label="Gearbelt subscription status"
              placeholder="Select subscription status..."
              className="flex-1"
              value={value}
              onChange={onChange}
              options={Object.values(SubscriptionStatuses).map((value) => ({ label: value, value }))}
              error={!!error}
              isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="initialSubscriptionDate"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt initial subscription date</div>
            <DatePicker
              placeholder="Select initial subscription date..."
              date={value}
              setDate={(date) => onChange(date)}
              error={!!error}
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="subscriptionTerm"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt subscription term</div>
            <Select
              aria-label="Gearbelt subscription term"
              placeholder="Select subscription term..."
              className="flex-1"
              value={value}
              onChange={onChange}
              options={Object.values(SubscriptionTerms).map((value) => ({ label: value, value }))}
              error={!!error}
              isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="billingCycle"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt billing cycle</div>
            <Select
              aria-label="Gearbelt billing cycle"
              placeholder="Select billing cycle..."
              className="flex-1"
              value={value}
              onChange={onChange}
              options={Object.values(BillingCycles).map((value) => ({ label: value, value }))}
              error={!!error}
              isDisabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <h3 className="text-lg font-medium">Assets</h3>

      <Controller
        control={control}
        name="assetsPaid"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt - Assets (Paid)</div>
            <Input
              placeholder="Enter assets paid..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Label className="flex items-center gap-1">
        <div className="w-1/3 shrink-0">Gearbelt - Assets (Used)</div>
        <Input value={account?.billing?.gearbelt?.assetsUsed || ''} disabled placeholder="Gearbelt - Assets (Used)" className="flex-1" />
      </Label>

      <Controller
        control={control}
        name="toolsAndEquipmentPaid"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Gearbelt - Tools and Equipment (Paid)</div>
            <Input
              placeholder="Enter tools and equipment paid..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)}
            />
          </Label>
        )}
      />

      <Label className="flex items-center gap-1">
        <div className="w-1/3 shrink-0">Gearbelt - Tools and Equipment (Used)</div>
        <Input value={account?.billing?.gearbelt?.toolsAndEquipmentUsed || 0} disabled placeholder="Gearbelt - Tools and Equipment (Used)" className="flex-1" />
      </Label>

      {user?.roles.includes(PermissionRoles.SitemateSuperUser) && (
        <div className="col-12">
          <Button type="submit" variant="success" className="mt-2" disabled={!isValid || isSubmitting || !isDirty} loading={isSubmitting}>
            Save changes
          </Button>
        </div>
      )}
    </form>
  );
}

export default SettingsSitemateStartGearbeltBillingPage;
